import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService } from '../core/app.service';
import { SearchService } from '../core/search.service';
import { Address } from 'ngx-google-places-autocomplete/objects/address';
import { ScopeCapabilityTypeLookupValue } from '../api/model/scope-capability/scope-capability-type.model';

@Component({
  selector: 'app-search-results-location',
  templateUrl: './search-results-location.component.html',
  styleUrls: ['./search-results-location.component.css']
})
export class SearchResultsLocationComponent implements OnInit {

  q = '';
  lat?: number;
  lng?: number;
  country = '';

  isLoading = true;
  results: any[] = [];
  baseResults: any[] = [];

  // Filters
  localFilter = true;
  longDistanceFilter = true;
  internationalFilter = true;

  // Sort
  currentSort: 'nearest' | 'alpha' = 'nearest';

  // Paging
  page = 1;
  pageSize = 10;

  todayDate = new Date();
  currYear = this.todayDate.getFullYear();

  complianceLink?: string;

  options = {
    fields: ['name', 'geometry.location', 'address_components', 'formatted_address']
  };

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private service: AppService,
    private searchService: SearchService
  ) { }

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe(params => {
      this.q = params.q || '';
      this.lat = params.lat ? parseFloat(params.lat) : undefined;
      this.lng = params.lng ? parseFloat(params.lng) : undefined;
      this.country = params.country || '';

      if (this.q) {
        this.performSearch();
        this.getSearchQueryLink(this.q);
      } else {
        this.isLoading = false;
      }
    });
  }

  performSearch(): void {
    this.isLoading = true;
    const params = {
      fromLocation: this.q,
      fromLatitude: this.lat,
      fromLongitude: this.lng,
      fromCountry: this.country,
      spc: '10,11', // Assuming this is for IAMTrusted
      size: 100, // Get a good batch for filtering/sorting
      page: 0
    };

    this.searchService.searchServiceProviders(params).subscribe(response => {
      this.baseResults = response.body || [];
      if (this.baseResults.length === 0) {
        this.router.navigate(['/search-location-no-results'], {
          queryParams: { q: this.q, lat: this.lat, lng: this.lng, country: this.country }
        });
        return;
      }
      this.applyFiltersAndSort();
      this.isLoading = false;
    }, error => {
      console.error('Search failed', error);
      this.isLoading = false;
    });
  }

  applyFiltersAndSort(): void {
    let filtered = [...this.baseResults];

    // Filter by scope (any-of)
    filtered = filtered.filter(sp => {
      if (!sp.scopeCapabilities) return false;
      const scopes = sp.scopeCapabilities.map(sc => sc.capabilityType.lookupvalue);

      const matchLocal = this.localFilter && scopes.includes(ScopeCapabilityTypeLookupValue.Local);
      const matchLong = this.longDistanceFilter && scopes.includes(ScopeCapabilityTypeLookupValue.LongDistance);
      const matchIntl = this.internationalFilter && scopes.includes(ScopeCapabilityTypeLookupValue.International);

      return matchLocal || matchLong || matchIntl;
    });

    // Sort
    if (this.currentSort === 'nearest') {
      // If we have lat/lng, the backend might have already sorted,
      // but if we are sorting client-side or if backend didn't, we might need logic.
      // Usually geodistance sort is done by backend.
    } else {
      filtered.sort((a, b) => {
        const nameA = (a.companylegalname || '').toLowerCase();
        const nameB = (b.companylegalname || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
    }

    this.results = filtered;
    this.page = 1; // Reset to first page on filter/sort change
  }

  toggleFilter(type: 'local' | 'long' | 'intl'): void {
    if (type === 'local') this.localFilter = !this.localFilter;
    if (type === 'long') this.longDistanceFilter = !this.longDistanceFilter;
    if (type === 'intl') this.internationalFilter = !this.internationalFilter;
    this.applyFiltersAndSort();
  }

  resetFilters(): void {
    this.localFilter = true;
    this.longDistanceFilter = true;
    this.internationalFilter = true;
    this.applyFiltersAndSort();
  }

  setSort(sort: 'nearest' | 'alpha'): void {
    this.currentSort = sort;
    this.applyFiltersAndSort();
  }

  showMore(): void {
    this.page++;
  }

  get displayedResults() {
    return this.results.slice(0, this.page * this.pageSize);
  }

  showContactModal = false;
  selectedSP: any;
  contactMethod: 'phone' | 'text' | 'email' = 'phone';
  contactName = '';
  contactDate = '';
  contactPhone = '';
  contactEmail = '';
  contactComments = '';
  toLocation = '';
  contactErrors: any = {};
  isSubmitting = false;
  submitSuccess = false;
  submitError = false;

  openContactModal(sp: any, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.selectedSP = sp;
    this.contactErrors = {};
    this.submitSuccess = false;
    this.submitError = false;
    this.loadSavedContactDetails();
    this.showContactModal = true;
  }

  loadSavedContactDetails() {
    const saved = localStorage.getItem('contactMeDetails');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        if (data.name) this.contactName = data.name;
        if (data.phone) this.contactPhone = data.phone;
        if (data.email) this.contactEmail = data.email;
        if (data.date) this.contactDate = data.date;
        if (data.comments) this.contactComments = data.comments;
        if (data.method) this.contactMethod = data.method;
        if (data.toLocation) this.toLocation = data.toLocation;
      } catch (e) { console.error('Error parsing saved details', e); }
    }
  }

  saveContactDetails() {
    const data = {
      name: this.contactName,
      phone: this.contactPhone,
      email: this.contactEmail,
      date: this.contactDate,
      comments: this.contactComments,
      method: this.contactMethod,
      toLocation: this.toLocation
    };
    localStorage.setItem('contactMeDetails', JSON.stringify(data));
  }

  closeContactModal() {
    this.showContactModal = false;
  }

  setContactMethod(method: 'phone' | 'text' | 'email') {
    this.contactMethod = method;
    this.saveContactDetails();
  }

  submitContactForm() {
    this.contactErrors = {};
    let hasError = false;

    if (!this.contactName || !this.contactName.trim()) {
      this.contactErrors.name = true;
      hasError = true;
    }
    if (this.contactMethod === 'email') {
      if (!this.contactEmail || !this.contactEmail.trim() || !this.validateEmail(this.contactEmail)) {
        this.contactErrors.email = true;
        hasError = true;
      }
    } else {
      if (!this.contactPhone || !this.contactPhone.trim()) {
        this.contactErrors.phone = true;
        hasError = true;
      }
    }

    if (hasError) {
      return;
    }

    this.isSubmitting = true;
    this.submitSuccess = false;
    this.submitError = false;

    const contactData = {
      name: this.contactName,
      fromLocation: this.q,
      toLocation: this.toLocation,
      plannedMoveDate: this.contactDate,
      contactMethod: this.contactMethod,
      phone: this.contactPhone,
      email: this.contactEmail,
      comments: this.contactComments
    };

    this.service.submitContactForm(this.selectedSP.id, contactData).subscribe(
      () => {
        this.isSubmitting = false;
        this.submitSuccess = true;
        setTimeout(() => {
          this.closeContactModal();
          this.submitSuccess = false;
        }, 10000);
      },
      (error) => {
        this.isSubmitting = false;
        this.submitError = true;
        console.error('Error submitting contact form', error);
      }
    );
  }

  validateEmail(email: string) {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  }

  public handleAddressChange(address: Address) {
    this.lat = address.geometry.location.lat();
    this.lng = address.geometry.location.lng();
    this.q = address.formatted_address;
    const filtered_array = address?.address_components?.filter(function (address_component) {
      return address_component.types.includes('country');
    });
    this.country = filtered_array?.length ? filtered_array[0].long_name : '';
  }

  search(): void {
    if (!this.q) return;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { q: this.q, lat: this.lat, lng: this.lng, country: this.country },
      queryParamsHandling: 'merge'
    });
  }

  clearSearch(): void {
    this.q = '';
    this.lat = undefined;
    this.lng = undefined;
    this.country = '';
  }

  getSearchQueryLink(loc: any): void {
    if (loc) {
      const splitted = loc.split(',');
      if (splitted.length > 2) {
        const code = splitted[1].trim();
        const state = code.split(' ');
        this.getComplianceLink(state[0]);
      } else if (splitted.length === 2) {
        const country = splitted[1].trim();
        if (country === 'USA') {
          const code = splitted[0].trim();
          this.getComplianceLink(code);
        }
      }
    }
  }

  getComplianceLink(code: any): void {
    const STATE_TYPE_ID = 51;
    this.service.licensingCompliance(STATE_TYPE_ID, code).subscribe(
      res => {
        const lookup = res.body;
        if (lookup.flex3 && lookup.flex3.length > 1) {
          this.complianceLink = lookup.flex3;
        }
      }
    );
  }

  isDabMover(sp: any): boolean {
    if (!sp || !sp.memberAssociations) return false;
    return sp.memberAssociations.some(ma => ma.memberAssociation && ma.memberAssociation.id === 927);
  }

  getOrderedScopeCapabilities(scopeCapabilities: any) {
    const ordered = [];
    if (!scopeCapabilities) return ordered;

    Object.values(ScopeCapabilityTypeLookupValue).forEach((val) => {
      const sc = scopeCapabilities.find(c => c.capabilityType.lookupvalue === val);
      if (sc) ordered.push(sc);
    });
    return ordered;
  }
}
